import React, { useEffect, useLayoutEffect, useState } from 'react';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    IconButton,
    Link,
    Tooltip,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ROUTES } from '../../types/global/routes.types';
import { getOffsiteCampaignDetails, bulkRescindCampaignParticipants } from '../../redux/buzzbridge/actions';
import {
    currentCampaignSelector,
    gettingCampaignDetailsSelector,
    bulkRescindingParticipantsSelector,
    bulkRescindLastSkippedSelector,
} from '../../redux/buzzbridge/selectors';
import { BRAND_LUXE_PRIMARY, BRAND_LUXE_SECONDARY, BRAND_SURFACE_CONTAINER_LOW } from './brandDashboardTokens';
import { StatusChip } from './BrandCampaignDetailPage';
import { ApplicationCard } from './campaignDetail/CampaignParticipants';
import { RescindApprovalDialog } from './campaignDetail/RescindApprovalDialog';
import { ConfirmDialog } from '../../components/ConfirmDialog/ConfirmDialog';
import type { ICampaignParticipantDetail } from '../../redux/buzzbridge/types';

function canRescindApproval(creator: ICampaignParticipantDetail): boolean {
    const status = String(creator.status || 'approved').toLowerCase();
    return status === 'approved' || status === 'in_progress';
}

type AcceptedCreatorFocus = { participantId: string; scrollY: number };

function focusStorageKey(campaignId: string) {
    return `accepted-creator-focus:${campaignId}`;
}

function readAcceptedCreatorFocus(campaignId: string): AcceptedCreatorFocus | null {
    try {
        const raw = sessionStorage.getItem(focusStorageKey(campaignId));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as AcceptedCreatorFocus;
        if (!parsed?.participantId) return null;
        return { participantId: parsed.participantId, scrollY: Number(parsed.scrollY) || 0 };
    } catch {
        return null;
    }
}

function storeAcceptedCreatorFocus(campaignId: string, participantId: string) {
    const focus: AcceptedCreatorFocus = { participantId, scrollY: window.scrollY };
    try {
        sessionStorage.setItem(focusStorageKey(campaignId), JSON.stringify(focus));
    } catch {
        /* ignore */
    }
}

function restoreAcceptedCreatorFocus(participantId: string, scrollY: number) {
    window.scrollTo(0, scrollY);
    const el = document.getElementById(`accepted-creator-${participantId}`);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const inView = rect.top >= 72 && rect.bottom <= window.innerHeight - 16;
    if (!inView) el.scrollIntoView({ block: 'center' });
}

export default function BrandCampaignAcceptedCreatorsPage() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { campaignId } = useParams<{ campaignId: string }>();
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];

    const campaign = useSelector(currentCampaignSelector);
    const loading = useSelector(gettingCampaignDetailsSelector);
    const bulkRescinding = useSelector(bulkRescindingParticipantsSelector);
    const bulkRescindSkipped = useSelector(bulkRescindLastSkippedSelector);

    const [rejectCreator, setRejectCreator] = useState<ICampaignParticipantDetail | null>(null);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [confirmRescind, setConfirmRescind] = useState(false);
    const [highlightedId, setHighlightedId] = useState<string | null>(() =>
        campaignId ? readAcceptedCreatorFocus(campaignId)?.participantId ?? null : null,
    );

    const openWorkspace = (participantId: string) => {
        if (!campaignId) return;
        storeAcceptedCreatorFocus(campaignId, participantId);
        setHighlightedId(participantId);
        navigate(
            ROUTES.BRAND_CAMPAIGN_PARTICIPANT.replace(':campaignId', campaignId).replace(
                ':participantId',
                participantId,
            ),
        );
    };

    const creators: ICampaignParticipantDetail[] = campaign?.participants ?? [];

    useEffect(() => {
        if (campaignId) dispatch(getOffsiteCampaignDetails({ campaignId, asBrandHost: true }));
    }, [campaignId, dispatch]);

    useLayoutEffect(() => {
        if (loading || !campaign || !campaignId || !highlightedId) return;
        const saved = readAcceptedCreatorFocus(campaignId);
        if (!saved || saved.participantId !== highlightedId) return;
        restoreAcceptedCreatorFocus(highlightedId, saved.scrollY);
        const frame = requestAnimationFrame(() => restoreAcceptedCreatorFocus(highlightedId, saved.scrollY));
        return () => cancelAnimationFrame(frame);
    }, [loading, campaign, campaignId, highlightedId, creators.length]);

    const toggleSelect = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const clearSelection = () => setSelectedIds(new Set());

    const handleBulkRescind = () => {
        if (!campaignId || bulkRescinding) return;
        setConfirmRescind(true);
    };

    const confirmBulkRescind = () => {
        if (!campaignId) return;
        dispatch(bulkRescindCampaignParticipants({ campaignId, participantIds: [...selectedIds] }));
        setConfirmRescind(false);
        clearSelection();
    };

    if (loading || !campaign) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress sx={{ color: BRAND_LUXE_PRIMARY }} />
            </Box>
        );
    }

    const rescindableCreators = creators.filter(canRescindApproval);

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 3, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
            {/* Bulk rescind overlay */}
            {bulkRescinding && (
                <Box
                    sx={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 1400,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        bgcolor: 'rgba(0,0,0,0.6)',
                        backdropFilter: 'blur(4px)',
                    }}>
                    <CircularProgress size={52} thickness={3} sx={{ color: BRAND_LUXE_PRIMARY }} />
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 14,
                            color: 'rgba(255,255,255,0.7)',
                            letterSpacing: '0.04em',
                        }}>
                        Removing creators…
                    </Typography>
                </Box>
            )}

            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3, '& .MuiBreadcrumbs-separator': { color: t.form_label } }}>
                <Link
                    component={RouterLink}
                    to={ROUTES.BRAND_CAMPAIGNS}
                    underline="hover"
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 13,
                        color: t.primary_white,
                        '&:hover': { color: BRAND_LUXE_PRIMARY },
                    }}>
                    Campaigns
                </Link>
                <Link
                    component={RouterLink}
                    to={ROUTES.BRAND_CAMPAIGN_DETAIL.replace(':campaignId', campaignId!)}
                    underline="hover"
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 13,
                        color: t.primary_white,
                        '&:hover': { color: BRAND_LUXE_PRIMARY },
                    }}>
                    {campaign.title}
                </Link>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: BRAND_LUXE_PRIMARY }}>
                    Accepted Creators
                </Typography>
            </Breadcrumbs>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 5 }}>
                <IconButton
                    onClick={() => navigate(ROUTES.BRAND_CAMPAIGN_DETAIL.replace(':campaignId', campaignId!))}
                    sx={{
                        color: BRAND_LUXE_PRIMARY,
                        bgcolor: 'rgba(255,201,101,0.08)',
                        '&:hover': { bgcolor: 'rgba(255,201,101,0.15)' },
                    }}>
                    <ArrowBackIcon />
                </IconButton>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: { xs: 22, md: 28 },
                                color: '#fff',
                                letterSpacing: '-0.02em',
                            }}>
                            {campaign.title}
                        </Typography>
                        <StatusChip status={campaign.status} />
                        {campaign.funded && (
                            <Chip
                                label="Funded"
                                size="small"
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 11,
                                    height: 24,
                                    bgcolor: `${BRAND_LUXE_SECONDARY}18`,
                                    color: BRAND_LUXE_SECONDARY,
                                }}
                            />
                        )}
                    </Box>
                    <Typography
                        sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 14, color: 'rgba(255,255,255,0.5)' }}>
                        Created{' '}
                        {new Date(campaign.createdAt!).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                        })}
                    </Typography>
                </Box>
            </Box>

            {campaign.feature_image && (
                <Box
                    sx={{
                        height: 200,
                        borderRadius: 3,
                        overflow: 'hidden',
                        bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                        mb: 4,
                    }}>
                    <Box
                        component="img"
                        src={campaign.feature_image}
                        alt=""
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Typography
                    sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 20, color: '#fff', letterSpacing: '-0.01em' }}>
                    Accepted Creators
                </Typography>
                <Chip
                    label={`${creators.length}`}
                    size="small"
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 10,
                        height: 24,
                        bgcolor: `${BRAND_LUXE_PRIMARY}18`,
                        color: BRAND_LUXE_PRIMARY,
                    }}
                />
            </Box>

            {/* Skipped warning */}
            {bulkRescindSkipped.length > 0 && (
                <Alert
                    icon={<WarningAmberIcon fontSize="small" />}
                    severity="warning"
                    sx={{
                        mb: 2,
                        bgcolor: 'rgba(255,152,0,0.1)',
                        border: '1px solid rgba(255,152,0,0.25)',
                        color: '#ffb74d',
                        borderRadius: 2,
                        '& .MuiAlert-icon': { color: '#ffb74d' },
                    }}>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, mb: 0.5 }}>
                        {bulkRescindSkipped.length} creator(s) could not be removed — they have active or completed
                        payouts
                    </Typography>
                    {bulkRescindSkipped.map(s => (
                        <Typography key={s.id} sx={{ fontSize: 12, color: 'rgba(255,183,77,0.8)' }}>
                            • {s.reason}
                        </Typography>
                    ))}
                </Alert>
            )}

            {/* Bulk rescind action bar */}
            <Collapse in={selectedIds.size > 0} unmountOnExit>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mb: 2,
                        px: { xs: 1.5, md: 2.5 },
                        py: { xs: 1, md: 1.5 },
                        borderRadius: 2,
                        bgcolor: 'rgba(255,80,80,0.07)',
                        border: '1px solid rgba(255,80,80,0.2)',
                    }}>
                    <Tooltip title={selectedIds.size === rescindableCreators.length ? 'Deselect all' : 'Select all'}>
                        <IconButton
                            onClick={() =>
                                selectedIds.size === rescindableCreators.length
                                    ? clearSelection()
                                    : setSelectedIds(new Set(rescindableCreators.map(c => c._id)))
                            }
                            size="small"
                            sx={{ p: 0.5, color: '#ff5050', '&:hover': { bgcolor: 'rgba(255,80,80,0.12)' } }}>
                            <DoneAllIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: { xs: 13, md: 14 },
                            color: '#fff',
                            whiteSpace: 'nowrap',
                        }}>
                        {selectedIds.size} selected
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Button
                        variant="outlined"
                        disabled={bulkRescinding}
                        onClick={handleBulkRescind}
                        sx={{
                            borderRadius: 999,
                            textTransform: 'uppercase',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: { xs: 10, md: 11 },
                            letterSpacing: '0.08em',
                            px: { xs: 2, md: 2.5 },
                            py: { xs: 0.6, md: 0.75 },
                            borderColor: 'rgba(255,80,80,0.4)',
                            color: '#ff5050',
                            whiteSpace: 'nowrap',
                            '&:hover': { borderColor: '#ff5050', bgcolor: 'rgba(255,80,80,0.08)' },
                            '&.Mui-disabled': { borderColor: 'rgba(255,80,80,0.15)', color: 'rgba(255,80,80,0.3)' },
                        }}>
                        {bulkRescinding ? (
                            <CircularProgress size={13} thickness={5} sx={{ color: '#ff5050' }} />
                        ) : (
                            `Reject ${selectedIds.size}`
                        )}
                    </Button>
                    <IconButton
                        onClick={clearSelection}
                        size="small"
                        sx={{ color: 'rgba(255,255,255,0.4)', p: 0.5, '&:hover': { color: '#fff' } }}>
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
            </Collapse>

            {creators.length === 0 ? (
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, borderRadius: 2, p: 6, textAlign: 'center' }}>
                    <Typography
                        sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 14, color: 'rgba(255,255,255,0.4)' }}>
                        No accepted creators yet
                    </Typography>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                        gap: 2,
                    }}>
                    {creators.map(c => (
                        <ApplicationCard
                            key={c._id}
                            applicant={c}
                            campaignId={campaignId!}
                            showActions={canRescindApproval(c)}
                            onReview={() => openWorkspace(c._id)}
                            onAccept={() => {}}
                            onReject={canRescindApproval(c) ? () => setRejectCreator(c) : undefined}
                            onClick={() => openWorkspace(c._id)}
                            highlighted={highlightedId === c._id}
                            selected={canRescindApproval(c) ? selectedIds.has(c._id) : undefined}
                            onToggleSelect={canRescindApproval(c) ? () => toggleSelect(c._id) : undefined}
                        />
                    ))}
                </Box>
            )}

            <ConfirmDialog
                open={confirmRescind}
                title={`Remove ${selectedIds.size} creator${selectedIds.size !== 1 ? 's' : ''} from roster?`}
                description="Their approval will be rescinded. Creators with active or completed payouts will be skipped and flagged."
                confirmLabel="Remove"
                destructive
                loading={bulkRescinding}
                onConfirm={confirmBulkRescind}
                onCancel={() => setConfirmRescind(false)}
            />

            <RescindApprovalDialog
                open={Boolean(rejectCreator)}
                onClose={() => setRejectCreator(null)}
                creator={rejectCreator}
                campaignId={campaignId!}
                requirements={campaign?.requirements}
            />
        </Box>
    );
}
